// SortHeader renders a <th> that toggles sort on click. Generic over
// the key type so each table can keep its own union of sortable keys
// (e.g. "wins" | "losses" | "rpi"). Ported from wsoccer-stats'
// SortHeader with the Tauri-only bits (align prop) retained since it
// transfers cleanly.

export type SortDir = "asc" | "desc";
export type SortSpec<K extends string> = { key: K; dir: SortDir };

export function SortHeader<K extends string>({
  label,
  sortKey,
  sort,
  onToggle,
  align,
  title,
}: {
  label: string;
  sortKey: K;
  sort: SortSpec<K> | null;
  onToggle: (k: K) => void;
  align?: "left" | "right";
  title?: string;
}) {
  const active = sort?.key === sortKey;
  const classes = ["sortable"];
  if (align === "right") classes.push("right");
  if (active) classes.push("sort-active");
  // aria-sort lets screen readers announce the current direction on
  // the header cell itself; "none" is the explicit state for a
  // sortable column that isn't the current one.
  const ariaSort: React.AriaAttributes["aria-sort"] = active
    ? sort!.dir === "asc"
      ? "ascending"
      : "descending"
    : "none";
  return (
    <th
      className={classes.join(" ")}
      role="button"
      tabIndex={0}
      aria-sort={ariaSort}
      onClick={() => onToggle(sortKey)}
      onKeyDown={(e) => {
        // Keyboard parity: Enter and Space activate the sort just
        // like a click. Space's default (page scroll) is suppressed
        // so a user tab-navigating the table doesn't bounce.
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onToggle(sortKey);
        }
      }}
      title={title ?? "Click to sort"}
    >
      <span className="th-label">{label}</span>
      <span className="sort-arrow" aria-hidden="true">
        {active ? (sort!.dir === "asc" ? "▲" : "▼") : "↕"}
      </span>
    </th>
  );
}

// toggleSort cycles asc → desc → (back to default). Numeric columns
// typically default to desc (highest first); text columns default to
// asc. Caller passes defaultDir.
export function toggleSort<K extends string>(
  current: SortSpec<K> | null,
  key: K,
  defaultDir: SortDir,
): SortSpec<K> {
  if (current?.key !== key) return { key, dir: defaultDir };
  return { key, dir: current.dir === "asc" ? "desc" : "asc" };
}
